import { createElement, useRef, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import Popover from '@mui/material/Popover';
import { FormField } from './FormField';
import { MatCalendar } from './MatCalendar';
import { overlayPaneProps } from './overlayPane';

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

export type DateValue = Dayjs | string | null | undefined;

interface DateFieldProps {
  value: DateValue;
  /** Emits a dayjs (which has `.format`, so `toIsoDate` works), or null when cleared/unparsable. */
  onChange: (value: Dayjs | null) => void;
  /** Display and typing format. Default en-GB `DD/MM/YYYY`; AddClient and the TB screens use `DD-MMM-YYYY`. */
  format?: string;
  /** Angular mostly used `readonly` inputs: the calendar is then the only way to pick. */
  readOnly?: boolean;
  /** The calendar never offers a year to choose (Add Client's Year End): it stays on the month view. */
  yearless?: boolean;
  disabled?: boolean;
  placeholder?: string;
  min?: Dayjs;
  max?: Dayjs;
  fieldClassName?: string;
  invalid?: boolean;
  /** Angular `(click)="picker.open()"` on the input. */
  openOnInputClick?: boolean;
  /** A read-only date can still be removed (QA report rows 7 and 8): a clear button shows beside the calendar while it has a value. */
  clearable?: boolean;
  id?: string;
  name?: string;
  ariaLabel?: string;
  /**
   * APT250: told about typed text the field cannot accept. Called with the text when a commit (blur or Enter) cannot
   * read it as a date, or the date is outside `min`/`max`, and with null once the text is a valid date again or empty
   * (also when a date is picked or cleared). A caller that passes it keeps the rejected text on show, so it can mark
   * the field invalid and refuse to save; without it, rejected text quietly reverts to the last valid date, as before.
   */
  onInvalidText?: (text: string | null) => void;
}

/** Height of the calendar popup (header + six week rows) that decides whether it fits below the field. */
const CALENDAR_HEIGHT = 354;

const toDayjs = (value: DateValue): Dayjs | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/** The calendar icon Material's datepicker toggle draws (18px via the ported overrides). */
function CalendarIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24px"
      height="24px"
      fill="currentColor"
      focusable="false"
      aria-hidden="true"
      className="mat-datepicker-toggle-default-icon"
    >
      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
    </svg>
  );
}

/** A small "x" for the optional clear button (same 24px box as the calendar icon). */
function ClearIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18px" height="18px" fill="currentColor" focusable="false" aria-hidden="true">
      <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
}

/**
 * `<mat-form-field><input matInput [matDatepicker]><mat-datepicker-toggle matSuffix>`: a bordered field with a
 * calendar toggle. The model is a dayjs (Angular: a Moment).
 */
export function DateField({
  value,
  onChange,
  format = 'DD/MM/YYYY',
  readOnly = false,
  yearless = false,
  disabled = false,
  placeholder,
  min,
  max,
  fieldClassName,
  invalid,
  openOnInputClick = false,
  clearable = false,
  id,
  name,
  ariaLabel,
  onInvalidText,
}: DateFieldProps) {
  const current = toDayjs(value);
  const shown = current ? current.format(format) : '';
  // Text typed but not yet committed; null means "show the model".
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? shown;
  const [focused, setFocused] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // the popup opens below the field, or above it when there is no room below (CDK connected positions)
  const [above, setAbove] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);

  const commitText = () => {
    const trimmed = text.trim();
    if (trimmed === '') {
      setDraft(null);
      onInvalidText?.(null);
      if (current) onChange(null);
      return;
    }
    const formatsToTry = [
      format,
      'DD/MM/YYYY',
      'DDMMYYYY',
      'D/M/YYYY',
      'DD-MM-YYYY',
      'D-M-YYYY',
      'DD.MM.YYYY',
      'D.M.YYYY',
      'DD-MMM-YYYY',
      'DD MMM YYYY',
      'YYYY-MM-DD',
      'DD/MM/YY',
      'DDMMYY',
      'DD-MM-YY',
    ];
    const parsed = dayjs(trimmed, formatsToTry, true);
    const accepted = parsed.isValid() && !(min && parsed.isBefore(min, 'day')) && !(max && parsed.isAfter(max, 'day'));
    if (!accepted) {
      // Invalid text resets the date: clearing draft causes the input to display `shown`. A caller told about rejected
      // text (APT250) keeps it on show instead, so the field can be marked invalid.
      if (onInvalidText) onInvalidText(trimmed);
      else setDraft(null);
      return;
    }
    setDraft(null);
    onInvalidText?.(null);
    if (!current || !parsed.isSame(current, 'day')) onChange(parsed);
  };

  /** A date picked or cleared outright replaces any rejected text. */
  const replaceText = (next: Dayjs | null) => {
    setDraft(null);
    onInvalidText?.(null);
    onChange(next);
  };

  const openCalendar = () => {
    const origin = fieldRef.current?.querySelector<HTMLElement>('.mat-mdc-form-field-flex') ?? fieldRef.current;
    if (disabled || !origin) return;
    const rect = origin.getBoundingClientRect();
    setAbove(window.innerHeight - rect.bottom < CALENDAR_HEIGHT && rect.top >= CALENDAR_HEIGHT);
    setAnchor(origin);
  };

  return (
    <div ref={fieldRef} style={{ display: 'contents' }}>
      <FormField
        className={fieldClassName}
        invalid={invalid}
        disabled={disabled}
        focused={focused}
        suffix={
          <>
            {clearable && current && !disabled && (
              <button
                type="button"
                className="mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base date-field-clear"
                aria-label="Clear date"
                title="Clear date"
                onClick={() => replaceText(null)}
              >
                <span className="mat-mdc-button-persistent-ripple mdc-icon-button__ripple"></span>
                <ClearIcon />
                <span className="mat-mdc-focus-indicator"></span>
                <span className="mat-mdc-button-touch-target"></span>
              </button>
            )}
            {createElement(
              'mat-datepicker-toggle',
              { className: `mat-datepicker-toggle${anchor ? ' mat-datepicker-toggle-active' : ''}` },
              <button
                type="button"
                className="mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
                aria-haspopup="dialog"
                aria-label="Open calendar"
                disabled={disabled}
                onClick={openCalendar}
              >
                <span className="mat-mdc-button-persistent-ripple mdc-icon-button__ripple"></span>
                <CalendarIcon />
                <span className="mat-mdc-focus-indicator"></span>
                <span className="mat-mdc-button-touch-target"></span>
              </button>,
            )}
          </>
        }
      >
        <input
          id={id}
          name={name}
          aria-label={ariaLabel}
          className="mat-mdc-input-element mdc-text-field__input mat-datepicker-input mat-mdc-form-field-input-control"
          autoComplete="off"
          readOnly={readOnly}
          disabled={disabled}
          placeholder={placeholder}
          value={text}
          onChange={(event) => setDraft(event.target.value)}
          onClick={openOnInputClick ? openCalendar : undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            if (!readOnly) commitText();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !readOnly) commitText();
          }}
        />
      </FormField>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: above ? 'top' : 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: above ? 'bottom' : 'top', horizontal: 'left' }}
        slotProps={{
          root: {
            slotProps: {
              backdrop: { invisible: true, className: 'cdk-overlay-backdrop mat-overlay-transparent-backdrop' },
            },
          },
          paper: { ...overlayPaneProps(), className: 'cdk-overlay-pane mat-datepicker-popup' },
        }}
      >
        {createElement(
          'mat-datepicker-content',
          { className: 'mat-datepicker-content mat-primary' },
          <div role="dialog" aria-modal="true" className="mat-datepicker-content-container">
            <MatCalendar
              selected={current}
              minDate={min}
              maxDate={max}
              monthYearLabel={yearless ? 'MMMM' : format === 'DD-MMM-YYYY' ? 'YYYY' : 'MMM YYYY'}
              yearless={yearless}
              onSelect={(picked) => {
                replaceText(picked);
                setAnchor(null);
              }}
            />
          </div>,
        )}
      </Popover>
    </div>
  );
}
